import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { approveLeave, fetchLeaves, fetchOtAllocationRegister, setOtAllocationStatus } from "../../../Moduels/attendance/attendanceApi";
import { approveWfRoster, fetchWfRosters } from "../../../Moduels/attendance/rosterApi";
import { decideWfApproval, fetchWfApprovals } from "../../../Moduels/attendance/wfApi";
import {
  approveSalaryBatchFinance,
  approveSalaryBatchHr,
  listSalaryBatches,
  rejectSalaryBatch,
} from "../../../Moduels/Bank/disbursementApi";
import {
  approveAdvance,
  approveFuelBill,
  getFuelBills,
  getOrgAdvances,
  rejectAdvance,
  rejectFuelBill,
} from "../../../Moduels/AllowancesBenefits/allowancesApi";
import { listChangeRequests, reviewChangeRequest } from "../../../Moduels/Bank/employeeBankingApi";
import { decideCompOffCredit, fetchCompOffCredits, fetchCompOffManage } from "../../../Moduels/leave/compOffApi";
import { decideEncashment, fetchEncashmentRequests } from "../../../Moduels/leave/encashmentApi";
import { listPayrollProcessApprovals, rejectPayrollProcessApproval } from "../../../Moduels/payroll/payrollApi";
import { writePayrollWorkflow } from "../../../Moduels/payroll/payrollWorkflow";
import { approveLeaveRequest, fetchLeaveRequests } from "../../../Moduels/leave/leaveApi";
import { findEmployeeById, formatEmployeeLabel } from "../../common/employeeUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import { isAdminLike, normalizeRole } from "../../../rbac/access";
import { extractApiErrorMessage } from "../../../utils/guidedErrors";
import { buildActionItems, useAttendanceApprovalCount } from "../../../services/actionItems";
import { dashboardOverviewQueryKey, getDashboardOverview, readDashboardCache } from "../../../services/dashboardApi";
import { useLeaveOrg } from "../Leave/useLeaveOrg";
import "./ApprovalsBoard.css";

const COLUMNS = [
  { id: "Pending", hint: "Waiting for a decision" },
  { id: "Approved", hint: "Accepted and in effect" },
  { id: "Rejected", hint: "Declined or cancelled" },
];

const TYPE_FILTERS = [
  { id: "", label: "All requests" },
  { id: "leave", label: "Leave" },
  { id: "attendance_leave", label: "Attendance leave" },
  { id: "comp_off", label: "Comp Off" },
  { id: "encashment", label: "Encashment" },
  { id: "attendance", label: "Attendance" },
  { id: "ot", label: "OT hours" },
  { id: "roster", label: "Roster" },
  { id: "payroll", label: "Payroll" },
  { id: "fuel", label: "Fuel" },
  { id: "advance", label: "Advance" },
  { id: "bank", label: "Bank change" },
];

const ATTENDANCE_TYPES = {
  ot_request: "OT request",
  regularization: "Regularization",
  attendance_regularization: "Regularization",
  punch_correction: "Punch correction",
  attendance_dispute: "Attendance dispute",
  missing_punch: "Missing punch",
  shift_swap: "Shift swap",
};

function messageOf(error, fallback) {
  return extractApiErrorMessage(error, fallback);
}

function asList(data, key) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function personLabel(name, code) {
  const label = String(name || "").trim();
  const employeeCode = String(code || "").trim();
  if (label && employeeCode && !label.includes(employeeCode)) return `${label} (${employeeCode})`;
  return label || employeeCode || "";
}

function formatCardDate(value) {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${parsed.getFullYear()}`;
}

function sortStamp(value) {
  if (!value) return "";
  const raw = String(value);
  return raw.length >= 10 ? raw : "";
}

function money(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
}

function columnFor(status) {
  const key = String(status || "").toLowerCase();
  if (["approved", "available", "applied", "closed"].includes(key)) return "Approved";
  if (["rejected", "cancelled", "canceled", "withdrawn"].includes(key)) return "Rejected";
  return "Pending";
}

function initials(name) {
  const parts = String(name || "")
    .replace(/\(.*?\)/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "") || "•").toUpperCase();
}

function card({
  source,
  id,
  typeKey,
  type,
  employeeName,
  employeeId,
  date,
  detail,
  status,
  column,
  canAct,
  canReject = true,
  needsReason = null,
  meta = {},
}) {
  return {
    key: `${source}:${id}`,
    source,
    id,
    typeKey,
    type,
    employeeName: employeeName || "",
    employeeId: employeeId || "",
    date: formatCardDate(date),
    sortDate: sortStamp(date),
    detail: detail || "",
    status: status || column,
    column,
    canAct: Boolean(canAct),
    canReject: Boolean(canReject),
    needsReason,
    meta,
  };
}

function leaveCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "pending").toLowerCase();
    const pending = status === "pending" || status === "escalated";
    const label =
      status === "escalated" || row.pending_approver_role === "hr"
        ? "Awaiting HR"
        : status === "pending" && row.pending_approver_role === "manager"
          ? "Awaiting manager"
          : status.charAt(0).toUpperCase() + status.slice(1);
    const span =
      row.start_date && row.end_date && row.start_date !== row.end_date
        ? `${formatCardDate(row.start_date)} to ${formatCardDate(row.end_date)}`
        : formatCardDate(row.start_date || row.end_date);
    const days = row.total_days != null ? `${row.total_days} day${Number(row.total_days) === 1 ? "" : "s"}` : "";
    return card({
      source: "leave",
      id: row.request_id,
      typeKey: "leave",
      type: "Leave",
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: row.applied_at || row.start_date,
      detail: [row.leave_type_name || row.leave_type_code, span, days, row.reason].filter(Boolean).join(" · "),
      status: label,
      column: columnFor(status === "escalated" ? "pending" : status),
      canAct: pending,
    });
  });
}

function attendanceLeaveCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "pending").toLowerCase();
    const span =
      row.start_date && row.end_date && String(row.start_date) !== String(row.end_date)
        ? `${formatCardDate(row.start_date)} to ${formatCardDate(row.end_date)}`
        : formatCardDate(row.start_date || row.end_date);
    return card({
      source: "attendance_leave",
      id: row.leave_id,
      typeKey: "attendance_leave",
      type: "Attendance leave",
      employeeId: row.employee_id,
      date: row.created_at || row.start_date,
      detail: [row.leave_type, span, row.reason].filter(Boolean).join(" · "),
      status: status.charAt(0).toUpperCase() + status.slice(1),
      column: columnFor(status),
      canAct: status === "pending",
    });
  });
}

function compOffCards(rows, fallbackStatus) {
  return rows.map((row) => {
    const status = String(row.status || fallbackStatus || "pending_approval");
    const actionable = ["pending_approval", "needs_review", "capped"].includes(status);
    const label =
      status === "pending_approval"
        ? "Pending"
        : status === "available"
          ? "Approved"
          : status.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
    return card({
      source: "comp_off",
      id: row.credit_id,
      typeKey: "comp_off",
      type: "Comp Off",
      employeeName: row.employee_name || row.name || "",
      employeeId: row.employee_id,
      date: row.earned_date || row.attendance_date || row.earning_date,
      detail: [row.category_label || row.reason, row.quantity ? `${row.quantity} day` : ""].filter(Boolean).join(" · "),
      status: label,
      column: actionable ? "Pending" : columnFor(status === "available" ? "approved" : status),
      canAct: actionable && Boolean(row.credit_id),
      needsReason: "always",
    });
  });
}

function encashmentCards(rows, role) {
  const admin = isAdminLike(role);
  const hr = normalizeRole(role) === "hr" || admin;
  return rows.map((row) => {
    const status = String(row.status || "");
    const canAct =
      (admin && (status === "pending_manager" || status === "pending_hr")) ||
      (hr && !admin && status === "pending_hr");
    const label =
      status === "pending_manager"
        ? "Awaiting manager"
        : status === "pending_hr"
          ? "Awaiting HR"
          : status.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
    return card({
      source: "encashment",
      id: row.encashment_id,
      typeKey: "encashment",
      type: "Encashment",
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: row.requested_at || row.approved_at,
      detail: [`${row.days} day${Number(row.days) === 1 ? "" : "s"}`, money(row.approved_amount ?? row.estimated_amount)]
        .filter(Boolean)
        .join(" · "),
      status: label || "Pending",
      column: status.startsWith("pending") ? "Pending" : columnFor(status),
      canAct,
      needsReason: "reject",
      meta: { stage: status === "pending_hr" ? "hr" : "manager" },
    });
  });
}

function attendanceCards(rows) {
  return rows.map((row) => {
    const payload = row.payload_json || {};
    const type = ATTENDANCE_TYPES[row.entity_type] || "Attendance request";
    const status = String(row.status || "pending").toLowerCase();
    const hours = payload.hours ?? payload.ot_hours;
    return card({
      source: "attendance",
      id: row.request_id,
      typeKey: "attendance",
      type,
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: payload.work_date || payload.workDate || row.created_at,
      detail: [
        hours != null && hours !== "" ? `${hours} hour${Number(hours) === 1 ? "" : "s"}` : "",
        payload.requested_status,
        payload.reason || payload.notes,
      ]
        .filter(Boolean)
        .join(" · "),
      status: status.charAt(0).toUpperCase() + status.slice(1),
      column: columnFor(status),
      canAct: status === "pending",
    });
  });
}

function fuelCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "PENDING").toUpperCase();
    const column = status === "APPROVED" ? "Approved" : status === "REJECTED" ? "Rejected" : "Pending";
    return card({
      source: "fuel",
      id: row.id,
      typeKey: "fuel",
      type: "Fuel",
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: row.bill_date || row.created_at,
      detail: [money(row.amount), row.bill_number ? `Bill ${row.bill_number}` : "", row.remarks].filter(Boolean).join(" · "),
      status: column,
      column,
      canAct: status === "PENDING",
      needsReason: "reject",
    });
  });
}

function advanceCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "").toLowerCase();
    const approval = String(row.approval_status || "").toLowerCase();
    let column = "Pending";
    let label = "Pending";
    if (approval === "rejected" || status === "cancelled") {
      column = "Rejected";
      label = status === "cancelled" ? "Cancelled" : "Rejected";
    } else if (approval === "approved" || status === "applied" || status === "closed") {
      column = "Approved";
      label = status === "applied" ? "Applied" : status === "closed" ? "Closed" : "Approved";
    }
    return card({
      source: "advance",
      id: row.id,
      typeKey: "advance",
      type: "Advance",
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: row.advance_date || row.created_at,
      detail: [money(row.amount), row.note].filter(Boolean).join(" · "),
      status: label,
      column,
      canAct: status === "pending" && approval !== "approved",
      needsReason: "reject",
    });
  });
}

function payrollProceedCards(rows, role) {
  const admin = isAdminLike(role);
  return rows.map((row) => {
    const extras = [
      Number(row.advances) > 0 ? `Advances ${money(row.advances)}` : "",
      Number(row.overtime) > 0 ? `Overtime ${money(row.overtime)}` : "",
      Number(row.leave_encashment) > 0 ? `Encashment ${money(row.leave_encashment)}` : "",
      Number(row.fuel_allowance) > 0 ? `Fuel ${money(row.fuel_allowance)}` : "",
    ].filter(Boolean);
    const period =
      row.period_start && row.period_end
        ? `${formatCardDate(row.period_start)} to ${formatCardDate(row.period_end)}`
        : "";
    return card({
      source: "payroll_process",
      id: row.payroll_run_id,
      typeKey: "payroll",
      type: "Payroll proceed",
      employeeName: row.requested_by_name || "HR",
      date: row.requested_at,
      detail: [
        row.payroll_month || "Payroll",
        period,
        row.employee_count != null ? `${row.employee_count} employees` : "",
        `Attendance pay ${money(row.attendance_pay) || "₹0"}`,
        ...extras,
      ]
        .filter(Boolean)
        .join(" · "),
      status: "Waiting for super admin",
      column: "Pending",
      canAct: admin,
      needsReason: "reject",
      meta: {
        payrollRunId: row.payroll_run_id,
        month: row.payroll_month || "Payroll",
        periodStart: row.period_start,
        periodEnd: row.period_end,
      },
    });
  });
}

function payrollCards(rows, role) {
  const admin = isAdminLike(role);
  const hr = normalizeRole(role) === "hr" || admin;
  return rows.map((row) => {
    const status = String(row.status || "").toLowerCase();
    const pendingHr = status === "draft" || status === "hr_pending";
    const pendingFinance = status === "finance_pending";
    const rejected = status === "rejected" || status === "cancelled";
    const label = status.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
    return card({
      source: "payroll",
      id: row.batch_id,
      typeKey: "payroll",
      type: "Payroll",
      employeeName: row.batch_ref || row.payroll_run_label || "Salary batch",
      date: row.created_at,
      detail: [
        row.period_label || row.payroll_run_label,
        row.total_employees != null ? `${row.total_employees} employees` : "",
        money(row.total_amount),
      ]
        .filter(Boolean)
        .join(" · "),
      status: label || "Pending",
      column: pendingHr || pendingFinance ? "Pending" : rejected ? "Rejected" : "Approved",
      canAct: (pendingHr && hr) || (pendingFinance && admin),
      needsReason: "reject",
      meta: { step: pendingFinance ? "finance" : "hr" },
    });
  });
}

function otCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "PENDING").toUpperCase();
    const column = status === "APPROVED" ? "Approved" : status === "REJECTED" || status === "CANCELLED" ? "Rejected" : "Pending";
    return card({
      source: "ot",
      id: row.attendance_id,
      typeKey: "ot",
      type: "OT hours",
      employeeName: personLabel(row.employee_name, row.employee_code),
      employeeId: row.employee_id,
      date: row.work_date,
      detail: [row.ot_hours != null ? `${row.ot_hours} hours` : "", row.source_label].filter(Boolean).join(" · "),
      status: column,
      column,
      canAct: status === "PENDING",
    });
  });
}

function rosterCards(rows) {
  return rows.flatMap((row) => {
    const status = String(row.status || "").toLowerCase();
    if (!status || status === "draft" || status === "archived") return [];
    const pending = status === "pending_approval";
    const rejected = status === "rejected";
    return [
      card({
        source: "roster",
        id: row.roster_plan_id,
        typeKey: "roster",
        type: "Roster",
        employeeName: row.name || "Shift roster",
        date: row.period_start || row.created_at,
        detail: [
          row.period_start && row.period_end
            ? `${formatCardDate(row.period_start)} to ${formatCardDate(row.period_end)}`
            : "",
          row.unit_label,
        ]
          .filter(Boolean)
          .join(" · "),
        status: pending ? "Pending" : status.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase()),
        column: pending ? "Pending" : rejected ? "Rejected" : "Approved",
        canAct: pending,
        canReject: false,
      }),
    ];
  });
}

function bankCards(rows) {
  return rows.map((row) => {
    const status = String(row.status || "pending").toLowerCase();
    return card({
      source: "bank",
      id: row.request_id,
      typeKey: "bank",
      type: "Bank change",
      employeeName: "",
      employeeId: row.employee_id,
      date: row.created_at || row.requested_effective_from,
      detail: [
        row.account_number_last4 ? `Account ••••${row.account_number_last4}` : "",
        row.requested_effective_from ? `From ${formatCardDate(row.requested_effective_from)}` : "",
        row.reason,
      ]
        .filter(Boolean)
        .join(" · "),
      status: status.charAt(0).toUpperCase() + status.slice(1),
      column: columnFor(status),
      canAct: status === "pending",
    });
  });
}

async function loadBucket(label, loader, mapRows) {
  try {
    const data = await loader();
    return { cards: mapRows(data), warning: "" };
  } catch (error) {
    return { cards: [], warning: `${label} could not be loaded. ${messageOf(error, "Try again.")}` };
  }
}

export default function Approvals() {
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const { employees } = useEmployees();
  const [requests, setRequests] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [focus, setFocus] = useState("");
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [prompt, setPrompt] = useState(null);
  const [reason, setReason] = useState("");
  const overviewQuery = useQuery({
    queryKey: dashboardOverviewQueryKey(role, "all"),
    queryFn: () => getDashboardOverview(undefined),
    enabled: Boolean(role),
    staleTime: 30_000,
    placeholderData: readDashboardCache,
    retry: 1,
  });
  // Every pending item across modules (same list as the dashboard card, unabridged).
  const attendanceApprovals = useAttendanceApprovalCount(Boolean(role));
  const attention = buildActionItems(overviewQuery.data, navigate, { attendanceApprovals });

  const load = useCallback(async () => {
    setLoading(true);
    setActionError("");
    const jobs = [
      loadBucket("Attendance leave", () => fetchLeaves(), (data) => attendanceLeaveCards(asList(data))),
      loadBucket(
        "Comp Off",
        async () => {
          const [pending, review, capped, rejected, approved] = await Promise.all([
            fetchCompOffManage("pending", 1, 100),
            fetchCompOffManage("needs_review", 1, 100),
            fetchCompOffManage("capped", 1, 100),
            fetchCompOffManage("rejected", 1, 100),
            fetchCompOffCredits({ status: "available", pageSize: 50 }),
          ]);
          return [
            ...compOffCards(asList(pending), "pending_approval"),
            ...compOffCards(asList(review), "needs_review"),
            ...compOffCards(asList(capped), "capped"),
            ...compOffCards(asList(rejected), "rejected"),
            ...compOffCards(asList(approved), "available"),
          ];
        },
        (rows) => rows
      ),
      loadBucket("Encashment", () => fetchEncashmentRequests({ limit: 100 }), (data) =>
        encashmentCards(asList(data), role)
      ),
      loadBucket(
        "Attendance",
        async () => {
          const groups = await Promise.all(
            ["pending", "approved", "rejected"].map((statusFilter) => fetchWfApprovals({ statusFilter }))
          );
          return groups.flat();
        },
        (rows) => attendanceCards(rows)
      ),
      loadBucket("Fuel", () => getFuelBills({ limit: 100 }), (data) => fuelCards(asList(data, "bills"))),
      loadBucket("Advance", () => getOrgAdvances({ limit: 100 }), (data) => advanceCards(asList(data, "advances"))),
      loadBucket("Bank change", () => listChangeRequests({ status: "all", limit: 200 }), (data) =>
        bankCards(asList(data))
      ),
      loadBucket("Payroll", async () => {
        const res = await listSalaryBatches();
        return payrollCards(asList(res?.data ?? res), role);
      }, (rows) => rows),
      loadBucket("Payroll proceed", async () => {
        const res = await listPayrollProcessApprovals();
        return payrollProceedCards(asList(res?.data ?? res, "items"), role);
      }, (rows) => rows),
      loadBucket("OT hours", () => {
        const now = new Date();
        return fetchOtAllocationRegister({ year: now.getFullYear(), month: now.getMonth() + 1, page_size: 100 });
      }, (data) => otCards(asList(data, "rows"))),
      loadBucket("Roster", () => fetchWfRosters(), (data) => rosterCards(asList(data))),
    ];

    if (orgId) {
      jobs.unshift(
        loadBucket("Leave", () => fetchLeaveRequests({ organisation_id: orgId }), (data) => leaveCards(asList(data)))
      );
    }

    const settled = await Promise.all(jobs);
    const nextWarnings = settled.map((item) => item.warning).filter(Boolean);
    if (!orgId && !orgLoading) {
      nextWarnings.unshift("Leave requests need the organisation and could not be loaded.");
    }
    setRequests(settled.flatMap((item) => item.cards));
    setWarnings(nextWarnings);
    setLoading(false);
  }, [orgId, orgLoading, role]);

  useEffect(() => {
    if (orgLoading) return undefined;
    load();
    return undefined;
  }, [load, orgLoading]);

  const displayName = useCallback(
    (row) => {
      if (row.employeeName) return row.employeeName;
      const employee = findEmployeeById(employees, row.employeeId);
      return formatEmployeeLabel(employee) || "Employee";
    },
    [employees]
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests
      .filter((row) => (typeFilter ? row.typeKey === typeFilter : true))
      .filter((row) => {
        if (!query) return true;
        const haystack = `${displayName(row)} ${row.type} ${row.detail} ${row.status}`.toLowerCase();
        return haystack.includes(query);
      })
      .sort((a, b) => String(b.sortDate).localeCompare(String(a.sortDate)));
  }, [requests, typeFilter, search, displayName]);

  const needsReason = (row, action) =>
    row.needsReason === "always" || (row.needsReason === "reject" && action === "reject");

  const decide = async (row, action, note) => {
    const trimmed = String(note || "").trim();
    if (needsReason(row, action) && !trimmed) {
      setActionError("Add a reason before confirming this decision.");
      return;
    }
    setBusyKey(row.key);
    setActionError("");
    setNotice("");
    try {
      if (row.source === "leave") {
        await approveLeaveRequest(row.id, {
          decision: action === "approve" ? "approved" : "rejected",
          notes: trimmed || undefined,
        });
      } else if (row.source === "attendance_leave") {
        await approveLeave({
          leave_id: row.id,
          decision: action === "approve" ? "approved" : "rejected",
          notes: trimmed || null,
        });
      } else if (row.source === "comp_off") {
        await decideCompOffCredit(row.id, action === "approve" ? "approved" : "rejected", trimmed);
      } else if (row.source === "encashment") {
        await decideEncashment(row.id, {
          stage: row.meta.stage || "hr",
          decision: action === "approve" ? "approve" : "reject",
          notes: trimmed || undefined,
        });
      } else if (row.source === "attendance") {
        await decideWfApproval(row.id, {
          action: action === "approve" ? "approve" : "reject",
          comment: trimmed || undefined,
        });
      } else if (row.source === "fuel") {
        if (action === "approve") await approveFuelBill(row.id);
        else await rejectFuelBill(row.id, trimmed);
      } else if (row.source === "advance") {
        if (action === "approve") await approveAdvance(row.id);
        else await rejectAdvance(row.id, trimmed);
      } else if (row.source === "bank") {
        await reviewChangeRequest(row.id, {
          decision: action === "approve" ? "approved" : "rejected",
          reviewerComment: trimmed || undefined,
        });
      } else if (row.source === "payroll") {
        if (action === "approve") {
          const approve = row.meta.step === "finance" ? approveSalaryBatchFinance : approveSalaryBatchHr;
          await approve(row.id, { comment: trimmed || "Approved from Approvals" });
        } else {
          await rejectSalaryBatch(row.id, { comment: trimmed || "Rejected from Approvals" });
        }
      } else if (row.source === "payroll_process") {
        if (action === "approve") {
          writePayrollWorkflow({
            payrollRunId: row.id,
            payrollRunLabel: row.meta.month || "Payroll",
            payPeriodStart: row.meta.periodStart,
            payPeriodEnd: row.meta.periodEnd,
            payPeriodLabel: row.meta.month || "",
          });
          navigate("/payroll-process?tab=process");
          setPrompt(null);
          setReason("");
          setBusyKey("");
          return;
        }
        await rejectPayrollProcessApproval(row.id, trimmed || "Rejected from Approvals");
      } else if (row.source === "ot") {
        await setOtAllocationStatus({
          attendance_id: row.id,
          status: action === "approve" ? "APPROVED" : "REJECTED",
          remarks: trimmed || undefined,
        });
      } else if (row.source === "roster") {
        await approveWfRoster(row.id);
      }
      setPrompt(null);
      setReason("");
      setNotice(`${displayName(row)}'s ${row.type.toLowerCase()} was ${action === "approve" ? "approved" : "rejected"}.`);
      await load();
    } catch (error) {
      setActionError(messageOf(error, "The decision could not be saved."));
    } finally {
      setBusyKey("");
    }
  };

  const startAction = (row, action) => {
    setActionError("");
    if (needsReason(row, action)) {
      setPrompt({ key: row.key, action });
      setReason("");
      return;
    }
    decide(row, action, "");
  };

  const shownColumns = focus ? COLUMNS.filter((column) => column.id === focus) : COLUMNS;

  return (
    <div className="ap-page">
      <header className="ap-head">
        <div>
          <p className="ap-kicker">HR workspace</p>
          <h1 className="ap-title">Approvals</h1>
          <p className="ap-sub">
            Leave, attendance, overtime, roster, payroll, fuel, advance, and bank requests in one board.
            A payroll proceed card is the salary run HR sent for a super admin to approve.
          </p>
        </div>
        <button
          type="button"
          className="ap-refresh"
          onClick={() => {
            load();
            overviewQuery.refetch();
          }}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      {attention.length > 0 || (overviewQuery.isError && !overviewQuery.data) ? (
        <section className="ap-attention" aria-label="Action required">
          <div className="ap-attention-head">
            <h2>Action required</h2>
          </div>
          {overviewQuery.isError && !overviewQuery.data ? (
            <p className="ap-attention-error">
              Unable to load bank and compliance alerts.
              <button type="button" onClick={() => overviewQuery.refetch()}>Retry</button>
            </p>
          ) : (
            attention.map((item) => (
              <div className="ap-attention-row" key={item.key}>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                </div>
                <button type="button" className="ap-attention-go" onClick={item.onClick}>
                  {item.label}
                </button>
              </div>
            ))
          )}
        </section>
      ) : null}

      <div className="ap-stats">
        {COLUMNS.map((column) => {
          const count = visible.filter((row) => row.column === column.id).length;
          return (
            <button
              key={column.id}
              type="button"
              className={`ap-stat ap-stat--${column.id.toLowerCase()}${focus === column.id ? " is-on" : ""}`}
              onClick={() => setFocus((current) => (current === column.id ? "" : column.id))}
            >
              <div>
                <strong>{count}</strong>
                <span>{column.hint}</span>
              </div>
              <em>{column.id}</em>
            </button>
          );
        })}
      </div>

      <div className="ap-toolbar">
        <input
          type="search"
          className="ap-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by employee, request type, or detail"
          aria-label="Search employee requests"
        />
      </div>

      <div className="ap-filters" role="tablist" aria-label="Request type">
        {TYPE_FILTERS.map((filter) => {
          const active = typeFilter === filter.id;
          const count = requests.filter((row) => (filter.id ? row.typeKey === filter.id : true)).length;
          return (
            <button
              key={filter.id || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              className={`ap-chip${active ? " is-on" : ""}`}
              onClick={() => setTypeFilter(filter.id)}
            >
              <span>{filter.label}</span>
              <b>{count}</b>
            </button>
          );
        })}
      </div>

      {notice ? <p className="ap-alert ap-alert--ok">{notice}</p> : null}
      {actionError ? <p className="ap-alert ap-alert--bad">{actionError}</p> : null}
      {warnings.length ? (
        <div className="ap-alert ap-alert--warn">
          Some request lists could not be loaded. The rest of the board is still shown.
          <ul>
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={`ap-board${shownColumns.length === 1 ? " ap-board--one" : ""}`}>
        {shownColumns.map((column) => {
          const rows = visible.filter((row) => row.column === column.id);
          return (
            <section key={column.id} className="ap-col" aria-label={column.id}>
              <div className="ap-col-head">
                <div>
                  <h3>{column.id}</h3>
                  <p>{column.hint}</p>
                </div>
                <span className="ap-count">{rows.length}</span>
              </div>
              <div className="ap-list">
                {loading && requests.length === 0 ? <p className="ap-empty">Loading employee requests…</p> : null}
                {!loading && rows.length === 0 ? (
                  <p className="ap-empty">No {column.id.toLowerCase()} requests for this filter.</p>
                ) : null}
                {rows.map((row) => {
                  const name = displayName(row);
                  const asking = prompt?.key === row.key;
                  return (
                    <article key={row.key} className="ap-card">
                      <div className="ap-card-top">
                        <div className="ap-avatar" aria-hidden="true">{initials(name)}</div>
                        <div className="ap-who">
                          <div className="ap-name-row">
                            <h4 className="ap-name">{name}</h4>
                            <span className={`ap-pill ap-pill--${row.column}`}>{row.status}</span>
                          </div>
                          <p className="ap-meta">{row.type} · {row.date}</p>
                        </div>
                      </div>
                      {row.detail ? <p className="ap-detail">{row.detail}</p> : null}
                      {row.canAct ? (
                        asking ? (
                          <div className="ap-reason">
                            <input
                              aria-label="Decision reason"
                              placeholder={prompt.action === "reject" ? "Reason for rejection" : "Reason for approval"}
                              value={reason}
                              onChange={(event) => setReason(event.target.value)}
                            />
                            <div className="ap-actions">
                              <button
                                type="button"
                                className={`ap-btn ${prompt.action === "approve" ? "ap-btn--ok" : "ap-btn--no"}`}
                                disabled={busyKey === row.key || !reason.trim()}
                                onClick={() => decide(row, prompt.action, reason)}
                              >
                                {busyKey === row.key ? "Saving…" : prompt.action === "approve" ? "Confirm approve" : "Confirm reject"}
                              </button>
                              <button type="button" className="ap-btn ap-btn--ghost" onClick={() => setPrompt(null)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="ap-actions">
                            <button
                              type="button"
                              className="ap-btn ap-btn--ok"
                              disabled={busyKey === row.key}
                              onClick={() => startAction(row, "approve")}
                            >
                              {busyKey === row.key ? "Saving…" : row.source === "payroll_process" ? "Review & process" : "Approve"}
                            </button>
                            {row.canReject ? (
                              <button
                                type="button"
                                className="ap-btn ap-btn--no"
                                disabled={busyKey === row.key}
                                onClick={() => startAction(row, "reject")}
                              >
                                Reject
                              </button>
                            ) : null}
                          </div>
                        )
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
